import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { usePracticeSounds } from '../hooks/usePracticeSounds';
import useAudioRecorder from '../hooks/useAudioRecorder';
import UserProfileIcon from '../components/UserProfileIcon';
import Logo from '../components/Logo';
import RecordButton from '../components/RecordButton';
import FeedbackDisplay from '../components/FeedbackDisplay';
import {
  transcribeAudioPro,
  ProAnalysis,
} from '../services/api';
import { TranscriptionResult } from '../types';

// Category icons mapping
const getCategoryIcon = (categoryId: string): string => {
  const icons: Record<string, string> = {
    th_sounds: 'TH',
    r_sounds: 'R',
    l_sounds: 'L',
    vowel_length: 'Aa',
    schwa: 'ə',
    consonant_clusters: 'STR',
    word_stress: '!',
    sentence_rhythm: '~',
  };
  return icons[categoryId] || '?';
};

// Category colors mapping
const getCategoryColor = (categoryId: string): { bg: string; text: string; border: string; darkBg: string; darkBorder: string } => {
  const colors: Record<string, { bg: string; text: string; border: string; darkBg: string; darkBorder: string }> = {
    th_sounds: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', darkBg: '', darkBorder: '' },
    r_sounds: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', darkBg: '', darkBorder: '' },
    l_sounds: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', darkBg: '', darkBorder: '' },
    vowel_length: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', darkBg: '', darkBorder: '' },
    schwa: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', darkBg: '', darkBorder: '' },
    consonant_clusters: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200', darkBg: '', darkBorder: '' },
    word_stress: { bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-200', darkBg: '', darkBorder: '' },
    sentence_rhythm: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', darkBg: '', darkBorder: '' },
  };
  return colors[categoryId] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', darkBg: '', darkBorder: '' };
};

const PracticeSoundsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { getRecordingsRemaining, checkAndIncrementRecording } = useSubscription();

  const {
    mode,
    setMode,
    categories,
    selectedCategory,
    selectedText,
    availableTexts,
    userProfile,
    recommendedCategory,
    loadingCategories,
    loadingTexts,
    error,
    selectPracticeType,
    selectCategory,
    selectText,
    shuffleTexts,
    updateSessionResults,
    reset,
    goBack,
  } = usePracticeSounds();

  const {
    isRecording,
    audioBlob,
    recordingTime,
    startRecording,
    stopRecording,
    resetRecording,
  } = useAudioRecorder();

  // Results state
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState<TranscriptionResult | null>(null);
  const [proAnalysis, setProAnalysis] = useState<ProAnalysis | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);

  const remainingRecordings = getRecordingsRemaining();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Handle start recording
  const handleStartRecording = useCallback(async () => {
    if (remainingRecordings === 0) {
      return;
    }
    resetRecording();
    setTranscriptionResult(null);
    setProAnalysis(null);
    setProcessingError(null);
    await startRecording();
  }, [remainingRecordings, resetRecording, startRecording]);

  // Handle stop recording
  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // Process recording when blob is available
  const handleProcessRecording = useCallback(async () => {
    if (!audioBlob || !selectedText) return;

    setIsProcessing(true);
    setMode('processing');
    setProcessingError(null);

    try {
      const result = await transcribeAudioPro(audioBlob, selectedText.text);
      setTranscriptionResult(result);
      setProAnalysis(result.proAnalysis);

      // Increment recording usage
      await checkAndIncrementRecording();

      // Update user profile with session results
      const phonemeAnalysis = result.proAnalysis.wordAnalysis
        .flatMap(word => word.phonemes?.map(p => ({
          phoneme: p.phoneme,
          accuracy: p.accuracyScore,
          word: word.target,
        })) || []);

      await updateSessionResults(result.proAnalysis.overallScore, phonemeAnalysis);

      setMode('results');
    } catch (err) {
      console.error('Processing error:', err);
      setProcessingError('Failed to process recording. Please try again.');
      setMode('practice');
    } finally {
      setIsProcessing(false);
    }
  }, [audioBlob, selectedText, setMode, checkAndIncrementRecording, updateSessionResults]);

  // Practice again with same text
  const handlePracticeAgain = useCallback(() => {
    resetRecording();
    setTranscriptionResult(null);
    setProAnalysis(null);
    setProcessingError(null);
    setMode('practice');
  }, [resetRecording, setMode]);

  // Try different category
  const handleTryDifferentSound = useCallback(() => {
    resetRecording();
    setTranscriptionResult(null);
    setProAnalysis(null);
    setProcessingError(null);
    reset();
  }, [resetRecording, reset]);

  // Render Mode Selection
  const renderModeSelect = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Practice Sounds</h1>
        <p className="text-gray-600">Choose how you want to practice</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Choose Specific Sound */}
        <button
          onClick={() => selectPracticeType('specific')}
          className="bg-white rounded-2xl p-6 text-left border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all"
        >
          <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Choose a Sound</h3>
          <p className="text-gray-600 text-sm">
            Pick a specific sound category to practice (TH, R, L, vowels, etc.)
          </p>
          {recommendedCategory && (
            <div className="mt-3 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
              <span>Recommended: {recommendedCategory}</span>
            </div>
          )}
        </button>

        {/* Quick Practice */}
        <button
          onClick={() => selectPracticeType('random')}
          disabled={loadingTexts}
          className="bg-white rounded-2xl p-6 text-left border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all disabled:opacity-50"
        >
          <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Quick Practice</h3>
          <p className="text-gray-600 text-sm">
            Get a random practice text and discover which sounds you need to work on
          </p>
        </button>
      </div>

      {/* User Stats */}
      {userProfile && userProfile.totalSessions > 0 && (
        <div className="mt-8 bg-white rounded-xl p-4 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Your Progress</h4>
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-500">Sessions:</span>{' '}
              <span className="font-medium">{userProfile.totalSessions}</span>
            </div>
            <div>
              <span className="text-gray-500">Avg Score:</span>{' '}
              <span className="font-medium">{Math.round(userProfile.averageScore)}%</span>
            </div>
            {userProfile.problemSounds.length > 0 && (
              <div>
                <span className="text-gray-500">Focus Area:</span>{' '}
                <span className="font-medium text-amber-600">{userProfile.problemSounds[0].category}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Render Category Selection
  const renderCategorySelect = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Choose a Sound to Practice</h1>
        <p className="text-gray-600">Select a category to focus on</p>
      </div>

      {loadingCategories ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((category) => {
            const colors = getCategoryColor(category.id);
            const isRecommended = recommendedCategory === category.id;
            const isProblemArea = userProfile?.problemSounds.some(ps => ps.category === category.id);

            return (
              <button
                key={category.id}
                onClick={() => selectCategory(category)}
                className={`${colors.bg} ${colors.darkBg} rounded-xl p-4 text-left border-2 ${colors.border} ${colors.darkBorder} hover:shadow-md transition-all relative`}
              >
                {isRecommended && (
                  <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Recommended
                  </span>
                )}
                {isProblemArea && !isRecommended && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Focus
                  </span>
                )}
                <div className={`w-10 h-10 rounded-lg ${colors.bg} ${colors.darkBg} flex items-center justify-center mb-3 border ${colors.border} ${colors.darkBorder}`}>
                  <span className={`text-lg font-bold ${colors.text}`}>{getCategoryIcon(category.id)}</span>
                </div>
                <h3 className="font-semibold text-gray-800 mb-1">{category.name}</h3>
                <p className="text-xs text-gray-500 mb-2">{category.description}</p>
                <div className="flex flex-wrap gap-1">
                  {category.sounds.slice(0, 3).map((sound, i) => (
                    <span key={i} className={`text-xs ${colors.text} ${colors.bg} ${colors.darkBg} px-1.5 py-0.5 rounded`}>
                      {sound}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render Text Selection
  const renderTextSelect = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {selectedCategory?.name} Practice
        </h1>
        <p className="text-gray-600">Choose a text to practice</p>
      </div>

      {loadingTexts ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      ) : (
        <>
          <div className="space-y-4 mb-6">
            {availableTexts.map((text, index) => (
              <button
                key={index}
                onClick={() => selectText(text)}
                className="w-full bg-white rounded-xl p-4 text-left border-2 border-gray-200 hover:border-amber-400 hover:shadow-md transition-all"
              >
                <p className="text-gray-800 mb-2">{text.text}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>{text.wordCount} words</span>
                  <span>Level: {text.level}</span>
                  <span>Topic: {text.topic}</span>
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={shuffleTexts}
            disabled={loadingTexts}
            className="w-full py-3 text-amber-600 hover:text-amber-700 font-medium flex items-center justify-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Shuffle Texts
          </button>
        </>
      )}
    </div>
  );

  // Render Practice View
  const renderPractice = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Practice</h1>
        {selectedCategory && (
          <p className="text-gray-600">Focus: {selectedCategory.name}</p>
        )}
      </div>

      {/* Target Text */}
      <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
        <p className="text-lg text-gray-800 leading-relaxed">
          {selectedText?.text}
        </p>
        {selectedText?.targetSounds && selectedText.targetSounds.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">Target sounds:</p>
            <div className="flex flex-wrap gap-2">
              {selectedText.targetSounds.map((sound, i) => (
                <span key={i} className="text-sm bg-amber-100 text-amber-700 px-2 py-1 rounded">
                  {sound}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recording Controls */}
      <div className="flex flex-col items-center gap-4">
        <RecordButton
          isRecording={isRecording}
          isProcessing={isProcessing}
          recordingTime={recordingTime}
          onStart={handleStartRecording}
          onStop={handleStopRecording}
          disabled={remainingRecordings === 0}
        />

        {remainingRecordings === 0 && (
          <p className="text-sm text-red-600">Daily recording limit reached</p>
        )}

        {audioBlob && !isRecording && (
          <button
            onClick={handleProcessRecording}
            className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-xl transition-colors"
          >
            Analyze Recording
          </button>
        )}

        {processingError && (
          <p className="text-sm text-red-600">{processingError}</p>
        )}
      </div>
    </div>
  );

  // Render Processing
  const renderProcessing = () => (
    <div className="max-w-md mx-auto text-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">Analyzing your pronunciation...</h2>
      <p className="text-gray-600">This may take a few seconds</p>
    </div>
  );

  // Render Results
  const renderResults = () => (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Results</h1>
        {selectedCategory && (
          <p className="text-gray-600">Practice: {selectedCategory.name}</p>
        )}
      </div>

      {/* Score Summary */}
      {proAnalysis && (
        <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-amber-600">{Math.round(proAnalysis.overallScore)}%</p>
              <p className="text-sm text-gray-500">Overall</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">{Math.round(proAnalysis.accuracy)}%</p>
              <p className="text-sm text-gray-500">Accuracy</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-green-600">{Math.round(proAnalysis.fluency)}%</p>
              <p className="text-sm text-gray-500">Fluency</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-purple-600">{Math.round(proAnalysis.completeness)}%</p>
              <p className="text-sm text-gray-500">Completeness</p>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Display */}
      {transcriptionResult && (
        <div className="mb-6">
          <FeedbackDisplay
            transcriptionResult={transcriptionResult}
            targetText={selectedText?.text || ''}
          />
        </div>
      )}

      {/* Recommendations */}
      {userProfile && userProfile.problemSounds.length > 0 && (
        <div className="bg-amber-50 rounded-xl p-6 mb-6 border border-amber-200">
          <h3 className="font-semibold text-gray-800 mb-3">Sounds to Practice</h3>
          <div className="flex flex-wrap gap-2">
            {userProfile.problemSounds.slice(0, 5).map((ps, i) => (
              <span key={i} className="bg-white text-amber-700 px-3 py-1 rounded-full text-sm border border-amber-200">
                {ps.category}: {ps.sound}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={handlePracticeAgain}
          className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-xl transition-colors"
        >
          Practice Again
        </button>
        <button
          onClick={handleTryDifferentSound}
          className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl border border-gray-300 transition-colors"
        >
          Try Different Sound
        </button>
        <button
          onClick={() => navigate('/app')}
          className="px-6 py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors"
        >
          Back to Hub
        </button>
      </div>
    </div>
  );

  // Main render based on mode
  const renderContent = () => {
    switch (mode) {
      case 'mode_select':
        return renderModeSelect();
      case 'category_select':
        return renderCategorySelect();
      case 'text_select':
        return renderTextSelect();
      case 'practice':
        return renderPractice();
      case 'processing':
        return renderProcessing();
      case 'results':
        return renderResults();
      default:
        return renderModeSelect();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {mode !== 'mode_select' && mode !== 'processing' && (
              <button
                onClick={goBack}
                className="p-2 -m-2 text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <Logo size="md" onClick={() => navigate('/app')} />
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">Logout</span>
            </button>
            <UserProfileIcon />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            {error}
          </div>
        )}

        {renderContent()}
      </div>
    </div>
  );
};

export default PracticeSoundsPage;
